import React, { useState, useRef, useEffect } from 'react';

export interface SelectOption {
  id: string;
  name: string;
  secondaryLabel?: string;
}

interface SearchableSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((o) => o.id === value);

  const filtered = options.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (id: string) => {
    onChange(id);
    setIsOpen(false);
    setSearch('');
  };

  const toggle = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Trigger button */}
      <button
        type="button"
        disabled={disabled}
        onClick={toggle}
        className="gap-[8px] outline-none inline-flex justify-between items-center w-full px-[14px] py-[8px] border-[1px] border-darkcolor-300 text-[14px] font-medium rounded-[8px] text-neutral-25 bg-darkcolor-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ minHeight: 44 }}
      >
        <span className="truncate">
          {selectedOption ? selectedOption.name : placeholder}
        </span>
        <svg
          className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-[3px] rounded-[8px] shadow-lg border-[1px] border-darkcolor-400 bg-darkcolor-800"
          style={{ minWidth: 220 }}
        >
          {/* Search input */}
          <div className="block px-4 py-2 text-[14px] cursor-auto border-b-[1px] border-darkcolor-400 text-neutral-25 bg-darkcolor-800 rounded-t-[8px]">
            <input
              ref={inputRef}
              type="text"
              className="w-full bg-transparent outline-none text-[14px] text-neutral-25 placeholder-darkcolor-200 font-medium"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options list */}
          <ul className="overflow-y-auto" style={{ maxHeight: 220 }}>
            {filtered.length === 0 ? (
              <li className="px-4 py-[10px] text-[14px] text-neutral-25 bg-darkcolor-800">
                No results
              </li>
            ) : (
              filtered.map((option, index) => (
                <li
                  key={option.id}
                  className={`text-[14px] px-[10px] py-[10px] text-sm cursor-pointer text-neutral-25 flex justify-between bg-darkcolor-800 hover:bg-darkcolor-600 hover:text-neutral-25 ${
                    option.id === value ? 'bg-darkcolor-600' : ''
                  } ${index === filtered.length - 1 ? 'rounded-b-[8px]' : ''}`}
                  onClick={() => handleSelect(option.id)}
                >
                  <span
                    className="truncate ml-[8px]"
                    ref={(el) => {
                      if (el) el.title = el.scrollWidth > el.clientWidth ? option.name : '';
                    }}
                  >{option.name}</span>
                  {option.secondaryLabel ? (
                    <span className="ml-4 flex-shrink-0 text-[12px] text-neutral-200">
                      {option.secondaryLabel}
                    </span>
                  ) : (
                    <svg className="w-5 h-5 flex-shrink-0 text-neutral-25" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
